import React, { FC, useState, useCallback, useEffect } from 'react';
import classNames from 'classnames';

import IconChevronRight from 'ui/icons/ChevronRight';
import IconChevronDown from 'ui/icons/ChevronDown';
import IconChevronUp from 'ui/icons/ChevronUp';
import Button, { ButtonType } from 'ui/Button';

import { AccordionData } from './types';

import styles from './index.module.scss';

type OpenIconPlacementType = 'left' | 'right';

export type AccordionProps = AccordionData & {
  className?: string;
  titleClassName?: string;
  descriptionClassName?: string;
  descriptionHiddenClassName?: string;
  elementId: string;
  isOpen?: boolean;
  openIconPlacement?: OpenIconPlacementType;
  onClick?: (index: number) => void;
};

const getDescriptionClassName = (open: boolean, descriptionClassName?: string, descriptionHiddenClassName?: string) => {
  if (!open)
    return descriptionHiddenClassName
      ? `${styles.AccordionDescriptionHidden} ${descriptionHiddenClassName}`
      : styles.AccordionDescriptionHidden;

  if (descriptionClassName) return `${styles.AccordionDescriptionVisible} ${descriptionClassName}`;

  return styles.AccordionDescriptionVisible;
};

const getAdornmentLeft = (openIconPlacement: OpenIconPlacementType, open: boolean) => {
  if (openIconPlacement === 'right') return null;

  return open ? (
    <IconChevronDown className={styles.AccordionIcon} />
  ) : (
    <IconChevronRight className={styles.AccordionIcon} />
  );
};

const getAdornmentRight = (openIconPlacement: OpenIconPlacementType, open: boolean) => {
  if (openIconPlacement === 'left') return null;

  return open ? (
    <IconChevronUp className={styles.AccordionIcon} />
  ) : (
    <IconChevronDown className={styles.AccordionIcon} />
  );
};

const Accordion: FC<AccordionProps> = ({
  className,
  elementId,
  id,
  title,
  description,
  titleClassName,
  descriptionClassName,
  descriptionHiddenClassName,
  isOpen = true,
  openIconPlacement = 'left',
  onClick,
}) => {
  const [open, setOpen] = useState<boolean>(isOpen);

  const handleItemClick = useCallback(() => {
    setOpen((prev) => !prev);
    onClick?.(id);
  }, [id, onClick, setOpen]);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  return (
    <>
      <Button
        type={ButtonType.PLAIN}
        elementId={elementId}
        className={classNames(styles.Accordion, className)}
        adornmentLeftClassName={styles.AccordionAdornmentLeft}
        adornmentLeft={getAdornmentLeft(openIconPlacement, open)}
        adornmentRight={getAdornmentRight(openIconPlacement, open)}
        onClick={handleItemClick}>
        <div className={classNames(styles.AccordionTitle, titleClassName)}>{title}</div>
      </Button>
      <div
        className={classNames(
          styles.AccordionDescription,
          getDescriptionClassName(open, descriptionClassName, descriptionHiddenClassName)
        )}>
        {description}
      </div>
    </>
  );
};

export default Accordion;

export type { AccordionData } from './types';
